import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from '../static/styles/RedeemForm.module.css';
import MessagePopup from '../components/MessagePopup';
import { completeRedemption, requestRedeemOtp } from '../api/rewards';
import { isAuthenticated } from '../api/auth';
import { trackRewardRedemptionSuccess } from '../utils/analytics';
import ArrowLeft from '../static/img/backArrow.png'

import playstation5Img from "../static/img/Rewards/ps5.png";
import asusRogDeltaSCoreImg from "../static/img/Rewards/AsusROGDeltaSCoreGamingHeadphone.avif";
import edifierG1Img from "../static/img/Rewards/g2.png";
import asusTufKeyboardImg from "../static/img/Rewards/tufk3rd.png";
import bloodyB760Img from "../static/img/Rewards/b865n.png";
import asusGladiusImg from "../static/img/Rewards/gladius3.png";
import pubgImg from "../static/img/Rewards/pubguc.png";
import fortniteImg from "../static/img/Rewards/fbuks.png";
import minecraftImg from "../static/img/Rewards/minecraft.png";
import coinImg from "../static/img/Rewards/coin.png";
import zeroHeadphones from "../static/img/Rewards/zeroHeadphones.png";
import zeroEarbuds from "../static/img/Rewards/zeroEarbuds.png";
import zeroWatch from "../static/img/Rewards/zeroWatch.png";

const toCreditsNumber = (value, fallback = 0) => {
    const normalizedValue = typeof value === "string" ? value.replace(/[^\d.-]/g, "") : value;
    const numberValue = Number(normalizedValue);
    return Number.isFinite(numberValue) ? numberValue : fallback;
};

const getStoredProfileCredits = () => {
    try {
        const profileData = JSON.parse(sessionStorage.getItem("profileData") || "{}");
        return toCreditsNumber(profileData?.credits ?? profileData?.data?.credits, 0);
    } catch (error) {
        return 0;
    }
};

const RedeemForm = ({ reward }) => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [pubgId, setPubgId] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [popupMessage, setPopupMessage] = useState("");
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [pendingRedemptionData, setPendingRedemptionData] = useState(null);
    const [isOtpPopupOpen, setIsOtpPopupOpen] = useState(false);
    const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
    const [otpTimer, setOtpTimer] = useState(60);
    const otpInputRefs = useRef([]);

    const navigate = useNavigate();
    const location = useLocation();
    const rewardCredits = toCreditsNumber(location.state?.reward?.credits, 0);
    const ucredits = toCreditsNumber(location.state?.ucredits, getStoredProfileCredits());
    const leftCreds = toCreditsNumber(location.state?.leftCreds, Math.max(ucredits - rewardCredits, 0));
    useEffect(() => {
        document.title = "Redeem Form | Jazz ESL";
        if (!isAuthenticated()) {
            window.location.href = process.env.REACT_APP_LOGIN_URL;
        }
        if (!location.state) {
            navigate('/rewards');
            return;
        }

        setName(sessionStorage.getItem('username') || '')
    }, []);

    useEffect(() => {
        if (!isOtpPopupOpen) {
            return;
        }

        setOtpTimer(60);
        const timerId = setInterval(() => {
            setOtpTimer(prevTimer => Math.max(prevTimer - 1, 0));
        }, 1000);

        setTimeout(() => {
            otpInputRefs.current[0]?.focus();
        }, 0);

        return () => clearInterval(timerId);
    }, [isOtpPopupOpen]);

    const getErrorMessage = (error, fallback = "An error occurred. Please try again.") => (
        error?.data?.message ||
        error?.message ||
        error?.response?.data?.data?.message ||
        fallback
    );

    const getRewardImage = (rewardName) => {
        if (!rewardName) return "";

        const name = rewardName.toLowerCase();

        if (name.includes("playstation")) return playstation5Img;
        if (name.includes("asus rog") && name.includes("headphone"))
            return asusRogDeltaSCoreImg;
        if (name.includes("edifier")) return edifierG1Img;
        if (name.includes("zero lifestyle headphones")) return zeroHeadphones;
        if (name.includes("zero lifestyle watch")) return zeroWatch;
        if (name.includes("zero lifestyle earbuds")) return zeroEarbuds;
        if (name.includes("asus") && name.includes("keyboard"))
            return asusTufKeyboardImg;
        if (name.includes("bloody")) return bloodyB760Img;
        if (name.includes("asus rog") && name.includes("mouse"))
            return asusGladiusImg;
        if (name.includes("pubg")) return pubgImg;
        if (name.includes("fortnite")) return fortniteImg;
        if (name.includes("minecraft")) return minecraftImg;

        return "";
    };

    const submitRedemptionClaim = (data) => {
        setIsSubmitting(true);

        completeRedemption(data)
            .then(response => {
                if (response.status === 200) {
                    if (response.data.success) {
                        trackRewardRedemptionSuccess(
                            location.state.gtagItemName,
                            location.state.reward.id,
                            location.state.reward.credits || 0
                        );
                        navigate('/thankyou', {
                            state: {
                                reward: location.state.reward,
                                gameId: data.pubgId || "",
                                phone: data.phone,
                                email: data.email,
                                name: data.name
                            }
                        })
                    } else {
                        const errorMessage = response.data?.data?.message || "Redemption failed. Please try again.";
                        setPopupMessage(errorMessage);
                        setIsPopupOpen(true);
                        setIsSubmitting(false);
                    }
                } else {
                    setPopupMessage("Redemption failed. Please try again.");
                    setIsPopupOpen(true);
                    setIsSubmitting(false);
                }
            })
            .catch(error => {
                setPopupMessage(getErrorMessage(error));
                setIsPopupOpen(true);
                setIsSubmitting(false);
            });
    };

    const handleRedemption = () => {
        if (isSubmitting) {
            return;
        }

        const isIdRequired = location.state?.reward?.is_id_required;
        const isPubgIdValid = !isIdRequired || (pubgId && pubgId.length >= 9 && pubgId.length <= 13 && /^\d+$/.test(pubgId));
        const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        // const isFormValid = name && email && isPubgIdValid;
        const isFormValid = name && email && isEmailValid && isPubgIdValid;

        if (isFormValid) {
            setIsSubmitting(true);

            const rawMsisdn = sessionStorage.getItem('msisdn') || '';
            const phone = rawMsisdn.startsWith('92') ? rawMsisdn : `92${rawMsisdn.replace(/^0/, '')}`;
            const cleanName = name.trim();
            const cleanEmail = email.trim();
            const data = {
                item: location.state.reward.id,
                uuid: location.state.reward.uuid,
                name: cleanName,
                email: cleanEmail,
                phone,
            };

            if (isIdRequired) {
                data.pubgId = pubgId;
            }

            setPendingRedemptionData(data);
            requestRedeemOtp(data.item)
                .then(response => {
                    if (response.status === 200 && response.data.success) {
                        setOtpDigits(["", "", "", ""]);
                        setIsOtpPopupOpen(true);
                    } else {
                        const errorMessage = response.data?.data?.message || response.data?.message || "OTP request failed. Please try again.";
                        setPopupMessage(errorMessage);
                        setIsPopupOpen(true);
                    }
                    setIsSubmitting(false);
                })
                .catch(error => {
                    setPopupMessage(getErrorMessage(error, "OTP request failed. Please try again."));
                    setIsPopupOpen(true);
                    setIsSubmitting(false);
                });
        }
        else {
            const isIdRequired = location.state?.reward?.is_id_required;

            if (!name || !email) {
                setPopupMessage("Please fill in all required fields.");
            }
            else if (!isEmailValid) {
                setPopupMessage("Please enter a valid email address.");
            }
            else if (isIdRequired && !pubgId) {
                setPopupMessage("Please fill in PUBG ID.");
            }
            else if (isIdRequired && (pubgId.length < 9 || pubgId.length > 13 || !/^\d+$/.test(pubgId))) {
                setPopupMessage("PUBG ID must be 9-13 digits and contain only numbers.");
            }
            else {
                setPopupMessage("Please check your inputs.");
            }

            setIsPopupOpen(true);
        }
    }

    const handleOtpSubmit = () => {
        if (isSubmitting) {
            return;
        }

        const cleanOtp = otpDigits.join("");
        if (cleanOtp.length !== 4) {
            setPopupMessage("Please enter the 4-digit OTP.");
            setIsPopupOpen(true);
            return;
        }

        if (!pendingRedemptionData) {
            setPopupMessage("Please submit the redeem form again.");
            setIsPopupOpen(true);
            setIsOtpPopupOpen(false);
            return;
        }

        submitRedemptionClaim({
            ...pendingRedemptionData,
            otp: cleanOtp,
        });
    };

    const handleOtpDigitChange = (index, value) => {
        const digit = value.replace(/[^0-9]/g, "").slice(-1);
        const nextOtpDigits = [...otpDigits];
        nextOtpDigits[index] = digit;
        setOtpDigits(nextOtpDigits);

        if (digit && index < otpInputRefs.current.length - 1) {
            otpInputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, event) => {
        if (event.key === "Enter") {
            handleOtpSubmit();
            return;
        }

        if (event.key === "Backspace" && !otpDigits[index] && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (event) => {
        event.preventDefault();
        const pastedOtp = event.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 4);
        if (!pastedOtp) {
            return;
        }

        const nextOtpDigits = ["", "", "", ""];
        pastedOtp.split("").forEach((digit, index) => {
            nextOtpDigits[index] = digit;
        });
        setOtpDigits(nextOtpDigits);

        const nextFocusIndex = Math.min(pastedOtp.length, 3);
        otpInputRefs.current[nextFocusIndex]?.focus();
    };

    const handleOtpResend = () => {
        if (otpTimer > 0 || isSubmitting || !pendingRedemptionData) {
            return;
        }

        setIsSubmitting(true);
        requestRedeemOtp(pendingRedemptionData.item)
            .then(response => {
                if (response.status === 200 && response.data.success) {
                    setOtpDigits(["", "", "", ""]);
                    setOtpTimer(60);
                    otpInputRefs.current[0]?.focus();
                } else {
                    const errorMessage = response.data?.data?.message || response.data?.message || "OTP request failed. Please try again.";
                    setPopupMessage(errorMessage);
                    setIsPopupOpen(true);
                }
                setIsSubmitting(false);
            })
            .catch(error => {
                setPopupMessage(getErrorMessage(error, "OTP request failed. Please try again."));
                setIsPopupOpen(true);
                setIsSubmitting(false);
            });
    };

    if (!location.state) {
        return null;
    }

    return (
        <div className={styles.container}>

            {/* HEADER */}
            <div className={styles.navigationHeader}>
                <div
                    className={styles.navigationBack}
                    onClick={() => navigate(-1)}
                >
                    <img src={ArrowLeft} alt="<" />
                </div>
                <div className={styles.navigationTitle}>
                    <p>Step 1 of 2</p>
                    <h2 className={styles.heading}>redeem reward</h2>
                </div>
            </div>

            {/* REWARD CARD */}
            <div className={styles.rewardCard}>
                <div className={styles.rewardLeft}>
                    <div className={styles.rewardIcon}>
                        {/* PUBG<br />300 UC */}
                        <img
                            src={getRewardImage(location.state?.reward?.name)}
                            alt=""
                            className={styles.rewardImage} />
                    </div>
                </div>

                <div className={styles.rewardInfo}>
                    <span className={styles.badge}>POPULAR</span>
                    <h3>{location.state?.reward?.name}</h3>
                </div>

                <div className={styles.price}>
                    🪙 {location.state?.reward?.credits}
                </div>
            </div>

            {/* ACCOUNT DETAILS */}
            <div className={styles.section}>
                <span className={styles.sectionLabel}>ACCOUNT DETAILS</span>

                {/* NAME */}
                <div className={styles.inputGroup}>
                    <label>NAME</label>
                    <input
                        value={name}
                        onChange={e => setName(e.target.value.replace(/[^a-zA-Z0-9\s]/g, ''))}
                        placeholder="Enter your name"
                        required
                        minLength={3}
                        maxLength={50}
                    />
                </div>

                {/* EMAIL */}
                <div className={styles.inputGroup}>
                    <label>EMAIL</label>
                    <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value.trim())}
                        placeholder="Enter email address"
                        required
                        maxLength={100}
                        pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                    />
                </div>

                {/* PUBG ID */}
                {location.state?.reward?.is_id_required && (
                    <>
                        <div className={styles.inputGroup}>
                            <label>PUBG ID</label>
                            <input
                                type="text"
                                value={pubgId}
                                onChange={e => setPubgId(e.target.value.replace(/[^0-9]/g, ''))}
                                placeholder="e.g. 5847296301"
                                minLength={9}
                                maxLength={13}
                                required
                            />
                            <small>Find this in PUBG Mobile → Profile → Basic Info</small>
                        </div>

                        {/* <div className={styles.inputGroup}>
                            <label>CONFIRM PUBG ID</label>
                            <input
                                type="text"
                                placeholder="Re-enter your PUBG ID"
                                minLength={9}
                                maxLength={13}
                                required
                            />
                        </div> */}
                    </>
                )}

                {/* PHONE */}
                <div className={styles.inputGroup}>
                    <label>CONTACT NUMBER</label>
                    <input
                        value={sessionStorage.getItem('msisdn') || ''}
                        readOnly
                    />
                    <small>We will SMS your code here</small>
                </div>
            </div>

            {/* WALLET */}
            <div className={styles.walletCard}>
                <div>
                    <span>WALLET BALANCE</span>
                    <h3>{ucredits.toLocaleString()} credits</h3>
                </div>

                <div className={styles.afterRedeem}>
                    <span>AFTER REDEEM</span>
                    <br />
                    <strong>{leftCreds.toLocaleString()} credits</strong>
                </div>
            </div>

            {/* CTA */}
            <button
                className={styles.ctaBtn}
                onClick={handleRedemption}
                disabled={isSubmitting}
            >
                {isSubmitting ? 'PROCESSING...' : 'CONTINUE TO CONFIRM →'}
            </button>

            <p className={styles.note}>
                Delivery in {location.state?.reward?.game === null ? 'Within 7 days' : '24–48 hours'} · Auto-refund if undeliverable
            </p>

            <MessagePopup
                isOpen={isPopupOpen}
                message={popupMessage}
                onClose={() => setIsPopupOpen(false)}
            />

            {isOtpPopupOpen && (
                <div className={styles.otpOverlay} onClick={() => !isSubmitting && setIsOtpPopupOpen(false)}>
                    <div className={styles.otpPopup} onClick={(e) => e.stopPropagation()}>
                        <h3>ENTER OTP</h3>
                        <p>Enter the OTP sent to your registered contact number.</p>
                        <div className={styles.otpInputs}>
                            {otpDigits.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={element => {
                                        otpInputRefs.current[index] = element;
                                    }}
                                    type="text"
                                    inputMode="numeric"
                                    value={digit}
                                    onChange={e => handleOtpDigitChange(index, e.target.value)}
                                    onKeyDown={e => handleOtpKeyDown(index, e)}
                                    onPaste={handleOtpPaste}
                                    maxLength={1}
                                    aria-label={`OTP digit ${index + 1}`}
                                />
                            ))}
                        </div>
                        <button
                            className={styles.otpTimer}
                            type="button"
                            onClick={handleOtpResend}
                            disabled={otpTimer > 0 || isSubmitting}
                        >
                            {otpTimer > 0 ? `Resend OTP in ${otpTimer}s` : "Resend OTP"}
                        </button>
                        <div className={styles.otpActions}>
                            <button
                                className={styles.otpCancelBtn}
                                type="button"
                                onClick={() => setIsOtpPopupOpen(false)}
                                disabled={isSubmitting}
                            >
                                CANCEL
                            </button>
                            <button
                                className={styles.otpSubmitBtn}
                                type="button"
                                onClick={handleOtpSubmit}
                                disabled={isSubmitting || otpDigits.join("").length !== 4}
                            >
                                {isSubmitting ? 'VERIFYING...' : 'SUBMIT'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default RedeemForm;